import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import {
  Observable,
  Subject,
  catchError,
  exhaustMap,
  filter,
  finalize,
  fromEvent,
  map,
  of,
  timeout,
} from 'rxjs';

import {
  activeTripUrl,
  parseActiveTrip,
} from '../core/helpers/active-trip.helper';
import { User } from '../models/auth';
import { AuthService } from './auth.service';
import { EncryptionService } from './encryption.service';
import { EnquiryService } from './enquiries.service';

/** How long the tab must stay hidden before coming back counts as a "resume". */
const RESUME_THRESHOLD_MS = 60_000;

/** Longest an entry route waits for the lookup before opening normally. */
const ENTRY_LOOKUP_TIMEOUT_MS = 4_000;

/** Routes where an automatic redirect would interrupt the user. */
const EXCLUDED_URL_PREFIXES: readonly string[] = [
  '/auth',
  '/get-started',
  '/public',
  '/sp/chat/',
];

const ELIGIBLE_USER_TYPES: ReadonlySet<string> = new Set([
  'DRIVER',
  'SERVICE_PROVIDER',
]);

/**
 * Brings the driver's active trip to the front when the app is opened
 * (first navigation / after login) or resumed after being hidden for a while.
 * The backend (`/enquiry/active-trips`) picks the trip: in-progress before
 * not-started, immediate before scheduled.
 */
@Injectable({ providedIn: 'root' })
export class ActiveTripRedirectService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly encryption = inject(EncryptionService);
  private readonly enquiryService = inject(EnquiryService);

  private readonly checkRequests = new Subject<void>();
  private started = false;
  /** User id the app-open check last ran for; reset on logout so login re-triggers it. */
  private checkedUserId: string | null = null;
  private hiddenAt: number | null = null;

  start(): void {
    if (this.started || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.started = true;

    this.checkRequests
      .pipe(
        exhaustMap(() => this.resolveRedirect()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(url => {
        if (url && this.router.url.split(/[?#]/)[0] !== url) {
          this.router.navigateByUrl(url);
        }
      });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.onNavigationEnd());

    fromEvent(this.document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onVisibilityChange());

    // Started after the initial navigation already settled.
    if (this.router.navigated) {
      this.onNavigationEnd();
    }
  }

  /**
   * App-open check run by `activeTripEntryGuard` before a landing page
   * (profile, driver home, my trips) renders, so the user goes straight to
   * the active trip. Runs once per logged-in user; later visits resolve null
   * immediately. On timeout/error the post-navigation check takes over.
   */
  entryRedirect(): Observable<string | null> {
    const user = isPlatformBrowser(this.platformId) ? this.currentUser() : null;
    if (!user || this.checkedUserId === user.id) {
      return of(null);
    }
    this.checkedUserId = user.id;

    return this.findActiveTripUrl().pipe(
      timeout({ first: ENTRY_LOOKUP_TIMEOUT_MS }),
      catchError(() => {
        this.checkedUserId = null;
        return of(null);
      })
    );
  }

  private onNavigationEnd(): void {
    const user = this.currentUser();
    if (!user) {
      this.checkedUserId = null;
      return;
    }
    if (this.checkedUserId === user.id) {
      return;
    }
    this.checkedUserId = user.id;
    this.checkRequests.next();
  }

  private onVisibilityChange(): void {
    if (this.document.visibilityState === 'hidden') {
      this.hiddenAt = Date.now();
      return;
    }

    const hiddenAt = this.hiddenAt;
    this.hiddenAt = null;
    if (hiddenAt !== null && Date.now() - hiddenAt >= RESUME_THRESHOLD_MS) {
      this.checkRequests.next();
    }
  }

  private resolveRedirect(): Observable<string | null> {
    const user = this.currentUser();
    if (!user || this.isExcludedUrl(this.router.url)) {
      return of(null);
    }

    return this.findActiveTripUrl().pipe(
      // Re-check: the user may have navigated to an excluded page meanwhile.
      map(url => (url && !this.isExcludedUrl(this.router.url) ? url : null))
    );
  }

  private findActiveTripUrl(): Observable<string | null> {
    return this.enquiryService.getActiveTrip().pipe(
      map(data => {
        const trip = parseActiveTrip(data);
        return trip ? activeTripUrl(trip) : null;
      }),
      catchError(() => of(null))
    );
  }

  private currentUser(): User | null {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    const profile: User | null = this.encryption.decryptLocalStorage('profile');
    return profile && ELIGIBLE_USER_TYPES.has(profile.type) ? profile : null;
  }

  private isExcludedUrl(url: string): boolean {
    return EXCLUDED_URL_PREFIXES.some(prefix => url.startsWith(prefix));
  }
}
